<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>
    <vx-card class="mt-base mb-10">
      <h4 class="mb-5">Let’s start your virtual job experience in the Creative Industries!</h4>

      <h4 class="mb-5">Types of Jobs in the Creative Industries</h4>
      <p class="mb-5">
        The creative industries are a very fast-growing sector in the Australian economy. If you
        have an interest in using your creative and imaginative side, along with technical skills
        and knowledge, then this might be the industry for you.
      </p>
      <p class="mb-5">Most Jobs in the Creative Industries fall under:</p>

      <div class="vx-row my-5">
        <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
          <ul class="default-list">
            <li>Advertising and marketing</li>
            <li>Architecture</li>
            <li>Crafts</li>
            <li>Design: product, graphic and fashion design</li>
            <li>Film, TV, video, radio and photography</li>
            <li>IT, software and computer services</li>
            <li>Publishing</li>
            <li>Museums, galleries and libraries</li>
            <li>Music, performing and visual arts</li>
          </ul>
        </div>
        <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5 text-center">
          <img
            src="@/assets/images/experiences/creativeindustries/creativity.jpg"
            alt=""
            class="img-responsive mx-auto"
          />
        </div>
      </div>
      <p class="mb-5">
        In the design disciplines alone, there are 13 areas as described in the
        <a href="https://www.design.org.au/designindustry/design-disciplines" target="_blank"
          >Design Institute of Australia</a
        >. These are:
      </p>
      <vs-collapse accordion>
        <vs-collapse-item>
          <div slot="header">Industrial Design</div>
          <img
            src="@/assets/images/experiences/creativeindustries/industrial_design.jpg"
            alt=""
            class="my-2 ml-2 mb-2 float-right"
          />

          <p class="mb-5">
            Industrial Designers develop and prepare products for manufacture. They are particularly
            concerned with those aspects of products that relate to human usage and behaviour, and
            product appeal.
          </p>
          <p class="mb-5">Industrial designers are also known as product designers.</p>
          <p class="mb-5">
            They explore solutions to meet marketing, manufacturing and financial requirements and
            arrive at the optimum design of a product. They consider both functional and aesthetic
            aspects and pay particular attention to ergonomics, those factors that relate to ease of
            use and human behaviour.
          </p>
          <p class="mb-5">
            Product designers prepare models and prototypes to demonstrate and test products. They
            prepare drawings and illustrations of products to assist in the decision-making process
            and support marketing efforts.
          </p>
          <p class="mb-5">
            They select components and materials, resolve assembly and manufacturing details and
            produce digital and documentary instructions for others involved in the manufacturing
            process. They organise and oversee tooling to prepare for production and develop and
            oversee subsequent adjustments and refinements to the product.
          </p>
          <p class="mb-5">
            Industrial designers often work as part of a product development team. While other
            specialists such as electronics designers and mechanical engineers work on circuit
            boards and mechanical mechanisms the industrial designer may be working on the overall
            structure of the product, its appearance and the way in which the user will operate it.
          </p>
          <p class="mb-5">
            It is common for product designers to be employed within manufacturing companies. For
            manufacturers, the development and maintenance of product lines is a core requirement
            rather than an occasional need. Many industrial designers, however, work for businesses
            that consult to manufacturing companies, especially those companies that don’t maintain
            internal research and development facilities.
          </p>
          <p class="mb-5">
            The term industrial or product design can be used to refer to the development of any
            product type for manufacture. However, there are types of products, such as textiles and
            furniture, that have designers who specialise in their development.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Interior Design</div>
          <img
            src="@/assets/images/experiences/creativeindustries/interior_design.jpg"
            alt=""
            class="my-2 ml-2 mb-2 float-right"
          />

          <p class="mb-5">
            Interior designers plan and detail commercial and residential building interiors for
            effective use, with particular emphasis on space creation, space planning and factors
            that affect our responses to living and working environments.
          </p>
          <p class="mb-5">
            Interior designers are trained to consider the modification of the interior structure of
            the building rather than just refinishing and furnishing existing spaces.
          </p>
          <p class="mb-5">
            Good design can enable us to live and work more efficiently, comfortably, profitably,
            securely and pleasurably in a more aesthetically fulfilling and functional
            environment.<br />
            Interior designers plan space allocation, traffic flow, building services, furniture,
            fixtures, furnishings and surface finishes. They consider the purpose, efficiency,
            comfort, safety and aesthetic of interior spaces to arrive at an optimum design.
          </p>
          <p class="mb-5">
            They custom design or specify furniture, lighting, walls, partitions, flooring, colour,
            fabrics and graphics to produce an environment tailored to a purpose.
          </p>
          <p class="mb-5">
            An interior designer often works as part of a team that may include architects,
            builders, project managers, engineering consultants, shop fitters, cabinet makers,
            furniture suppliers and materials suppliers. They may be required to organise the
            modification of building structures, the purchasing of materials and furnishings and the
            contracting and supervision of the tradespeople required to implement a project.
          </p>
          <p class="mb-5">
            Many users of interior design services only require these skills occasionally. It is
            common for interior designers to be employed in consulting businesses such as interior
            design consultancies and architectural practices.
          </p>
          <p class="mb-5">
            Tertiary interior design education has an emphasis on commercial and industrial
            interiors such as offices, public buildings, hospitals, department stores and shops.
            However, the design of domestic interiors is also covered and is a common source of work
            for many interior designers in self employment.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Interior Decoration</div>
          <img
            src="@/assets/images/experiences/creativeindustries/interior_decoration.jpg"
            alt=""
            class="my-2 ml-2 mb-2 float-right"
          />

          <p class="mb-5">
            Interior decorators plan and prepare building interiors for effective use with
            particular emphasis on furnishings, finishes and aesthetic presentation.
          </p>
          <p class="mb-5">
            Interior decorators often work directly with the person who will occupy the space rather
            than working with other building or business professionals and must develop the skills
            to identify and accommodate another individual’s taste.
          </p>
          <p class="mb-5">
            They frequently have an extensive knowledge of historic furnishing styles and their
            relationship to architectural periods and employ a detailed understanding of the
            application and effect of colour and pattern.
          </p>
          <p class="mb-5">
            Interior decorators plan, arrange and style the space, finishes and furnishings. They
            consider the purpose, efficiency, comfort and aesthetic of interior spaces to arrive at
            an optimum design.
          </p>
          <p class="mb-5">
            They specify furniture, lighting, flooring, colour and fabrics to produce an environment
            tailored to a purpose.
          </p>
          <p class="mb-5">
            An interior decorator may work as part of a team that may include the owner/client, an
            architect, builders, shop fitters, cabinet makers, furniture suppliers and materials
            suppliers. They may be required to organise the purchasing of materials and furnishings
            and the contracting and supervision of the tradespeople required to implement a project.
          </p>
          <p class="mb-5">
            Many customers of interior decoration services are buying these services for their
            personal home and business environments and only require these skills occasionally. So,
            it is common for interior decorators to be employed in retail and consulting businesses
            such as interior decoration businesses that mix both design and the retail supply of
            furnishings and interior design consultancies.
          </p>
          <p class="mb-5">
            Tertiary interior decoration training has an emphasis on domestic interiors and small
            commercial interiors such as shops and private offices. However, training will also
            provide grounding in commercial and industrial interiors such as corporate offices and
            public buildings.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Graphic Design/Visual Communication</div>
          <img
            src="@/assets/images/experiences/creativeindustries/graphic_design.jpg"
            alt=""
            class="my-2 ml-2 mb-2 float-right"
          />

          <p class="mb-5">
            Graphic designers develop and prepare information for publication with particular
            emphasis on clarity of communication and the matching of information styles to audience
            requirements.
          </p>
          <p class="mb-5">
            Graphic designers sometimes refer to their area of specialisation as visual
            communication and some university courses use this as the course title.
          </p>
          <p class="mb-5">
            They information they deal with not only requires a sound understanding of text-based
            communication but also requires them to skilfully use the communication properties of
            symbols, colours and pictures.
          </p>
          <p class="mb-5">
            They prepare concept layouts and mock-ups to discuss project details with clients. They
            prepare or subcontract diagrams, illustrations and photography. They resolve all
            communication elements into a final format to suit the required physical or digital
            media.<br />
            They select paper and other printing materials, resolve manufacturing details and
            produce instructions for others involved in the reproduction process. They organise and
            oversee proofs and colour separations to prepare for printing and liaise with suppliers
            who specialise in the many forms of digital and computer-based information distribution
            mechanisms.
          </p>
          <p class="mb-5">
            Graphic designers often work as part of a project development team. While other
            specialists such as marketing managers and advertising specialists work on the
            strategies of distributing information to the marketplace and determining beneficial
            product positioning, the graphic designer may be working on the image and branding that
            will appeal to and attract the intended customer.
          </p>
          <p class="mb-5">
            The immense volume of visual material produced to support both commercial and cultural
            purposes means that the areas of employment open to graphic designers are very broad.
            There are many opportunities for employment within companies that have constant graphic
            requirements as well as great scope for self-employment selling services to businesses
            and organisations with only occasional needs.
          </p>
          <p class="mb-5">
            The rapidly developing areas of digital media relating to the internet and multimedia
            business presentations are opening up new areas of employment for graphic designers.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Textile Design</div>
          <img
            src="@/assets/images/experiences/creativeindustries/textile_design.jpg"
            alt=""
            class="my-2 ml-2 mb-2 float-right"
          />

          <p class="mb-5">
            Textile designers plan and develop patterns, knit and weave construction, prints,
            textures and illustrations for fabrics and other materials that require the development
            of patterned surfaces. They plan the way a fabric looks and performs.
          </p>
          <p class="mb-5">
            They design the structure of the fabric and make decisions about appropriate yarns,
            colour use, surface patterning, texture and finishing.
          </p>
          <p class="mb-5">
            Textile designers develop fabrics used in furniture, soft furnishings, clothing,
            vehicles and products such as luggage. They can apply the same skills to the development
            of patterns for wallpapers, laminates and patterned plastics.
          </p>
          <p class="mb-5">
            They design fabrics to satisfy marketing and manufacturing requirements. They balance
            aesthetic and functional aspects, they consider the nature of yarn types, thicknesses,
            weights and textures to produce fabrics to cost and production constraints.
          </p>
          <p class="mb-5">
            They prepare design concepts and assess them for market viability. They resolve the
            concepts into artworks and instructions suitable for a variety of fabric production and
            printing techniques. They develop colour specifications and multiple colourways for
            ranges of fabrics. They communicate with manufacturing and production personnel to
            resolve details for manufacture.
          </p>
          <p class="mb-5">
            They advise and liaise with others who work in industries where it is necessary to
            predict future colour trends. They monitor trends in industries such as interior design,
            automotive design and fashion and progressively evolve fabric styles to meet these
            specific needs.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Exhibition Design</div>
          <img
            src="@/assets/images/experiences/creativeindustries/exhibition_design.jpg"
            alt=""
            class="my-2 ml-2 mb-2 float-right"
          />

          <p class="mb-5">
            Exhibition designers design and organise the construction and installation of trade
            exhibitions, permanent shop displays, museum exhibits and interpretive displays.
          </p>
          <p class="mb-5">
            They use skills drawn from graphic, industrial (product) and interior design to attract,
            inform and involve an audience in the subjects that their clients employ them to
            present.
          </p>
          <p class="mb-5">
            In general, there is no specialised course leading to this occupation. Designers who
            have trained in industrial or product design, graphic design, interior design and
            digital areas like multimedia and web design all find roles in this occupation.
            Industrial designers use their ability to design form and mechanical detail, interior
            designers use their ability to design the function and aesthetic of spaces for human
            occupation, graphic designers use their skill at presenting messages in visual form.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Fashion Design</div>
          <img
            src="@/assets/images/experiences/creativeindustries/fashion_design.jpg"
            alt=""
            class="my-2 ml-2 mb-2 float-right"
          />

          <p class="mb-5">
            Fashion designers develop clothing, accessories, footwear and other items of personal
            apparel.
          </p>
          <p class="mb-5">
            They study the design and construction of clothing, its historical development and
            styles and the techniques and processes available for its manufacture. They rely heavily
            on illustration skills and the making of samples to communicate their designs.
          </p>
          <p class="mb-5">
            They prepare designs to meet marketing, manufacturing and financial requirements and
            arrive at the optimum design of a product. They consider both functional and aesthetic
            aspects and pay particular attention to the relationship of the apparel to the human
            form.
          </p>
          <p class="mb-5">
            They prepare clothing samples to demonstrate and test products. They prepare drawings
            and illustrations of clothing to assist in the decision-making process and support
            marketing efforts.
          </p>
          <p class="mb-5">
            They select materials, resolve assembly and manufacturing details and produce patterns
            for others involved in the manufacturing process.
          </p>
          <p class="mb-5">
            They prepare for production and develop and oversee subsequent adjustments and
            refinements to the product.
          </p>
          <p class="mb-5">
            It is common for fashion designers to be employed within manufacturing companies who
            develop mass produced clothing for the retail trade. Others work in small businesses
            making specialised lines of clothing for boutique retailers. Others produce custom
            tailored clothing for personal clients.
          </p>
          <p class="mb-5">
            Fashion designers may find employment in the wholesaling and retailing of clothing or in
            the media specialising in fashion and clothing.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Design Management</div>
          <img
            src="@/assets/images/experiences/creativeindustries/design_manager.jpg"
            alt=""
            class="my-2 ml-2 mb-2 float-right"
          />

          <p class="mb-5">
            With the integration of design into the business planning process of many large national
            and international companies and the identification of design as a major factor in
            competitive advantage the management of design has become a specialisation in its own
            right.
          </p>
          <p class="mb-5">
            As with other technical professions many senior professional designers find their work
            role gravitates toward supervision and management.
          </p>
          <p class="mb-5">
            A design manager has a broad understanding of the role that the many design
            specialisations can play in the growth and success of a business. A design manager is
            skilled at the strategic planning required to build a company’s success and
            profitability through the application of design.
          </p>
          <p class="mb-5">
            A comprehensive corporate strategy for design includes the role that design can play in
            every department of a company. This includes the overall presentation of the company
            through all aspects of media and visual presentation, the design of the work environment
            and the design of products.
          </p>
          <p class="mb-5">
            Research has demonstrated that design-led companies outperform their competition in both
            bull and bear markets.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Design Education</div>
          <img
            src="@/assets/images/experiences/creativeindustries/design_education.jpg"
            alt=""
            class="my-2 ml-2 mb-2 float-right"
          />

          <p class="mb-5">
            Design education has become a major growth area in both secondary and tertiary
            education.
          </p>
          <p class="mb-5">
            The education of designers requires teachers, lecturers and tutors with knowledge in the
            many subject areas that designers must study. It also requires experienced designers in
            each design discipline who are able to pass on the realities of professional practice.
          </p>
          <p class="mb-5">
            Design educators may have qualifications in a design discipline or in one of the
            subjects that make up the curriculum. They may additionally have qualifications in
            teaching.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Jewellery Design</div>
          <img
            src="@/assets/images/experiences/creativeindustries/jewellery_design.jpg"
            alt=""
            class="my-2 ml-2 mb-2 float-right"
          />

          <p class="mb-5">
            Jewellery designers conceptualise, prototype and detail for manufacture items of
            jewellery such as rings, brooches, bracelets, necklaces, watches, eyewear and earrings.
          </p>
          <p class="mb-5">
            They have specialised knowledge of the metals, jewels, precious stones and other
            materials associated with personal adornment. They may develop designs for mass or batch
            production, or they may develop special items to satisfy one-off commissions. They may
            also design other objects that use precious metals and jewelled decoration such as
            trophies, goblets, silverware and cutlery.
          </p>
          <p class="mb-5">
            They explore solutions to meet marketing, manufacturing and financial requirements and
            arrive at the optimum design of a product. They consider both functional and aesthetic
            aspects and pay particular attention to ergonomics, those factors that relate to the use
            of the item on the human form.
          </p>
          <p class="mb-5">
            They prepare models and prototypes to demonstrate and test products. They prepare
            drawings and illustrations of products to assist in the decision-making process and
            support marketing efforts.
          </p>
          <p class="mb-5">
            They select components and materials, resolve assembly and manufacturing details and, if
            they are working in a mass production environment, produce digital and documentary
            instructions for others involved in the manufacturing process. They organise and oversee
            tooling to prepare for production and develop and oversee subsequent adjustments and
            refinements to the product.
          </p>
          <p class="mb-5">
            Jewellery designers may be employed within manufacturing companies specialising in
            jewellery, or other decorative personal and home wares such as silverware, cutlery,
            eyewear, watches and trophies. Jewellery designers may also work in jewellery shops
            where custom and small batch production or repairs and adjustments are done. Some
            operate as independent designer/makers producing custom designs for clients or batches
            of jewellery for small retail outlets.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Furniture Design</div>
          <img
            src="@/assets/images/experiences/creativeindustries/furniture_design.jpg"
            alt=""
            class="my-2 ml-2 mb-2 float-right"
          />

          <p class="mb-5">
            Furniture designers develop and prepare furniture for manufacture. They are particularly
            concerned with those aspects of furniture that relate to human usage and behaviour,
            product appeal and fashion.
          </p>
          <p class="mb-5">
            Furniture design can be considered to be a specialist area of industrial design (product
            design). However, the specific ergonomic knowledge that a furniture designer must apply
            and the specialised construction methods and pre-manufactured components that undergo
            constant change in the industry make this a large area of specialisation.
          </p>
          <p class="mb-5">
            Furniture design is also undertaken by interior designers and, traditionally, by
            architects. These designers may be seeking a unique style of furnishing for an interior
            or architectural project. Or they may be operating as a design consultant to a
            manufacturer, or as a manufacturing entrepreneur on their own behalf.
          </p>
          <p class="mb-5">
            Furniture design has a rich history of styles and precedents and a close relationship
            with fashion which makes practice in this area distinctly different from many product
            design areas.
          </p>
          <p class="mb-5">
            Furniture designers explore solutions to meet marketing, manufacturing and financial
            requirements and arrive at the optimum design of a furniture item. They consider both
            functional and aesthetic aspects and pay particular attention to ergonomics, those
            factors that relate to ease of use and human behaviour.
          </p>
          <p class="mb-5">
            They prepare models and prototypes to demonstrate and test furniture. They prepare
            drawings and illustrations to assist in the decision-making process and support
            marketing efforts.
          </p>
          <p class="mb-5">
            They select components and materials, resolve assembly and manufacturing details and
            produce digital and documentary instructions for others involved in the manufacturing
            process. They organise and oversee tooling to prepare for production and develop and
            oversee subsequent adjustments and refinements to the furniture.
          </p>
          <p class="mb-5">
            Furniture designers may work as part of a product development team. While other
            specialists such as mechanical engineers and upholsterers may work on mechanical
            mechanisms and fabric construction patterns, the furniture designer may be working on
            the overall structure of the product and its appearance.
          </p>
          <p class="mb-5">
            It is common for furniture designers to be employed within manufacturing companies. For
            manufacturers, the development and maintenance of product lines is a core requirement
            rather than an occasional need. Some furniture designers, however, work for businesses
            that consult to manufacturing companies, especially those companies that don’t maintain
            internal research and development facilities.
          </p>
          <p class="mb-5">
            Some furniture designers operate as designer/maker businesses producing limited ranges
            of their own designs or designing custom furniture for individual clients.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Digital Media</div>
          <img
            src="@/assets/images/experiences/creativeindustries/digital_media.jpg"
            alt=""
            class="my-2 ml-2 mb-2 float-right"
          />

          <p class="mb-5">
            Multimedia and web designers develop and prepare information for digital publication
            with particular emphasis on clarity of communication and the matching of information
            styles to audience requirements.
          </p>
          <p class="mb-5">
            The information they deal with not only requires a sound understanding of text-based
            communication but also requires them to skilfully use the communication properties of
            symbols, colours, pictures, animation, video and sound.
          </p>
          <p class="mb-5">
            They prepare concept layouts and mock-ups to discuss project details with clients. They
            prepare or subcontract diagrams, illustrations and photography. They resolve all
            communication elements into a final format to suit the required digital media.
          </p>
          <p class="mb-5">
            Digital designers often work as part of a project development team. While other
            specialists such as copywriters, photographers, illustrators, sound recordists,
            composers and animators are preparing specific elements of the production the multimedia
            designer or web designer may be working on the overall presentation of the production,
            the layout of each page or section and the navigation methodology that will enable the
            user to access the digital publication.
          </p>
          <p class="mb-5">
            The immense volume of visual material produced to support both commercial and cultural
            purposes means that the areas of employment open to digital designers are very broad.
          </p>
          <p class="mb-5">
            There are many opportunities for employment within companies that have constant digital
            requirements as well as great scope for self employment selling services to businesses
            and organisations with only occasional needs.
          </p>
        </vs-collapse-item>
        <vs-collapse-item>
          <div slot="header">Other Design disciplines</div>

          <p class="mb-5">
            Computerisation and digital design software are causing new design specialisations and
            segmentation of existing disciplines.
          </p>
        </vs-collapse-item>
      </vs-collapse>

      <p class="mb-5">
        As you can see there are lots of different types of jobs available in this sector. Some of
        these jobs require formal qualifications and training, while others just require that you
        can demonstrate to the employer or client that you possess the necessary abilities. This is
        often done through the means of a portfolio.
      </p>
      <p class="mb-5">
        A portfolio is a compilation of materials and samples of work you’ve done in the past to
        show a potential employer or client what you’re capable of.
      </p>
    </vx-card>
    <vx-card>
      <p class="mb-5">
        In the following activities you will learn some of the main skills and important knowledge
        related to three common jobs in the Creative Industries sector:
      </p>
      <ul class="default-list">
        <li>Graphic Designer</li>
        <li>Interior Designer</li>
        <li>Photographer</li>
      </ul>
      <p class="mb-5">
        You can use the 3 tabs below to learn all about these three jobs/careers. After learning a
        little about the principles that underpin each of these jobs, you’ll get a chance to
        practise some of these principles and related skills by producing your own work. You will
        then be able to upload these samples to your digital portfolio to save for future reference.
      </p>
    </vx-card>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="The Graphic Designer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Description</u></heading>
            <p class="mb-5">
              Graphic Designers develop the overall layout and visual appearance of a wide range of
              print and electronic media. They design magazine covers, advertisements, web pages,
              signs, shirts, reports, logos, books, clip art, etc.
            </p>
            <heading size="h3" style-size="h5" class="mb-5 font-normal"
              ><u>Education Level</u></heading
            >
            <p class="mb-5">
              You usually need a bachelor’s degree or a diploma in graphic design, graphic arts, or
              another related field to work as a Graphic Designer.
            </p>
            <p class="mb-5">
              Formal study will help provide you with an understanding of relevant skills and
              theories. It will also help instil confidence in future potential employers or
              clients. Going the degree or diploma route is the safest way to find work as a Graphic
              Designer.
            </p>
            <p class="mb-5">
              However, it is sometimes possible to gain entry to the profession based on the
              strength of your portfolio and relevant experience.
            </p>
            <p class="mb-5">
              Before starting any course, you need to check it will provide you with the skills and
              qualifications you need. For more information on training pathways, visit:
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> to search
              and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
            <p class="mb-5">
              <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare
              Vocational Education and Training (VET) courses, providers, and student outcomes.
            </p>
            <p class="mb-5">
              <a href="https://www.aapathways.com.au/career-research/job-pathways" target="_blank"
                >AAPathways</a
              >
              website to explore Information and Communications Technology VET training pathways and
              Printing & Graphic Arts VET training pathways.
            </p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="The Interior Designer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Description</u></heading>
            <p class="mb-5">
              Interior Designers plan, design, detail and supervise the layout and decoration of the
              interiors of commercial, industrial, retail, and residential buildings. Interior
              Designers work to produce an environment tailored to a purpose, with particular
              emphasis on space creation, space planning, and factors that enhance living and
              working environments.
            </p>
            <heading size="h3" style-size="h5" class="mb-5 font-normal"
              ><u>Education Level</u></heading
            >
            <p class="mb-5">
              You usually need a formal qualification in interior design or architecture to work as
              an Interior Designer. University and Vocational Education and Training (VET) are both
              common study pathways.
            </p>
            <p class="mb-5">
              Before starting any course, you need to check it will provide you with the skills and
              qualifications you need. For more information on training pathways, visit:
            </p>
            <p class="mb-5">
              <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> to search
              and compare higher education courses.
            </p>
            <p class="mb-5">
              <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
              undergraduate and postgraduate student experiences and outcomes.
            </p>
            <p class="mb-5">
              <a href="https://www.myskills.gov.au/" target="_blank">My Skills</a> to compare
              Vocational Education and Training (VET) courses, providers, and student outcomes.
            </p>
            <p class="mb-5">
              <a href="https://www.aapathways.com.au/career-research/job-pathways" target="_blank"
                >AAPathways</a
              >
              website to explore Information and Communications Technology VET training pathways and
              Printing & Graphic Arts VET training pathways.
            </p>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="The Photographer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <heading size="h3" style-size="h5" class="mb-5 font-normal"><u>Description</u></heading>
            <p class="mb-5">
              Photographers use camera equipment to take still photographs. They can work for
              agencies or, more commonly, as freelancers. Photographers often specialise in specific
              areas. Some common areas of photography specialisation include landscape, fashion,
              portrait, and sport.
            </p>
            <heading size="h3" style-size="h5" class="mb-5 font-normal"
              ><u>Education Level</u></heading
            >
            <p class="mb-5">
              Though there are no formal training or qualification requirements to work as a
              photographer, you’ll need to be highly skilled at operating camera equipment and image
              editing software.
            </p>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>

    <h4 class="mb-5">Quick Details:</h4>

    <vs-tabs alignment="fixed" v-model="activeTab" class="mb-5 no-padding overwrite-tabs">
      <vs-tab label="The Graphic Designer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <ul class="default-list">
                  <li><strong>Size:</strong> This is a very large occupation.</li>
                  <li>
                    <strong>Earnings:</strong> Full-time workers on an adult wage earn around $1,346
                    per week (similar to the average of $1,460). Earnings tend to be lower when
                    starting and higher as experience grows.
                  </li>
                  <li>
                    <strong>Full-time:</strong> Many work full-time (70%, similar to the average of
                    66%).
                  </li>
                  <li>
                    <strong>Hours:</strong> Full-time workers spend around 43 hours per week at work
                    (compared to an average of 44 hours).
                  </li>
                  <li>
                    <strong>Age:</strong> The average age is 35 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender:</strong> 51% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <vs-alert color="dark" title="Caution" active="true" class="mb-5">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>

                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list">
                  <li>
                    Determining the objectives and constraints of the design brief by consulting
                    with clients and stakeholders
                  </li>
                  <li>Undertaking research and analysing functional communication requirements</li>
                  <li>Formulating design concepts for the subject to be communicated</li>
                  <li>
                    Preparing sketches, diagrams, illustrations, and layouts to communicate design
                    concepts
                  </li>
                  <li>
                    Negotiating design solutions with clients, management, sales, and production
                    staff
                  </li>
                  <li>
                    Selecting, specifying, or recommending functional and aesthetic materials and
                    media for publication, delivery, or display
                  </li>
                  <li>Detailing and documenting the selected design for production</li>
                  <li>Supervising or carrying out production in the chosen media</li>
                  <li>May archive information for future client use.</li>
                </ul>
                <p class="mb-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/creativeindustries/jo1.png"
                  class="mx-auto img-responsive"
                  alt="Graphic and Web Designers and Illustrators job outlook: $1,346 weekly pay, strong future growth, 55,700 workers, very high skill level, 70% full-time, 43 hours average full-time week, average age 35, 51% female."
                />
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="The Interior Designer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <ul class="default-list">
                  <li><strong>Size:</strong> This is a medium-sized occupation.</li>
                  <li>
                    <strong>Earnings:</strong> Full-time workers on an adult wage earn around $1,155
                    per week (below the average of $1,460). Earnings tend to be lower when starting
                    and higher as experience grows.
                  </li>
                  <li>
                    <strong>Full-time:</strong> Many work full-time (69%, similar to the average of
                    66%).
                  </li>
                  <li>
                    <strong>Hours:</strong> Full-time workers spend around 45 hours per week at work
                    (compared to an average of 44 hours).
                  </li>
                  <li>
                    <strong>Age:</strong> The average age is 38 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender:</strong> 76% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <vs-alert color="dark" title="Caution" active="true" class="mb-5">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>
                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list">
                  <li>
                    Determining the objectives and constraints of the design brief by consulting
                    with clients and stakeholders
                  </li>
                  <li>
                    Researching and analysing spatial, functional, efficiency, safety, and aesthetic
                    requirements
                  </li>
                  <li>Formulating design concepts for building interiors</li>
                  <li>
                    Preparing sketches, diagrams, illustrations, and plans to communicate design
                    concepts
                  </li>
                  <li>
                    Negotiating design solutions with clients, management, suppliers, and
                    construction staff
                  </li>
                  <li>
                    Selecting, specifying, and recommending functional and aesthetic materials,
                    furniture, and products for interiors
                  </li>
                  <li>Detailing and documenting selected design for construction</li>
                  <li>Supervising the construction of interiors</li>
                </ul>
                <p class="mb-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/creativeindustries/jo2.png"
                  alt="Interior Designers job outlook: $1,155 weekly pay, very strong future growth, 9,200 workers, high skill level, 69% full-time, 45 hours average full-time week, average age 38, 76% female."
                  class="mx-auto img-responsive"
                />
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
      <vs-tab label="The Photographer">
        <div class="py-2">
          <vx-card class="no-shadow">
            <div class="vx-row mt-5 flex justify-center">
              <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
                <ul class="default-list">
                  <li><strong>Size:</strong> This is a medium-sized occupation.</li>
                  <li>
                    <strong>Full-time:</strong> Around half work full-time (48%, less than the
                    average of 66%), showing there are many opportunities to work part-time).
                  </li>
                  <li>
                    <strong>Hours:</strong> Full-time workers spend around 46 hours per week at work
                    (compared to an average of 44 hours).
                  </li>
                  <li>
                    <strong>Age:</strong> The average age is 38 years (compared to the average of 40
                    years).
                  </li>
                  <li>
                    <strong>Gender:</strong> 49% of workers are female (compared to the average of
                    48%).
                  </li>
                </ul>
                <vs-alert color="dark" title="Caution" active="true" class="mb-5">
                  The Australian jobs market is changing in response to the COVID-19 pandemic. These
                  estimates do not take account of the impact of COVID-19. They may not reflect the
                  current jobs market and should be used and interpreted with extreme caution.
                </vs-alert>
                <h4 class="mb-5">Tasks</h4>
                <ul class="default-list">
                  <li>
                    Consulting with clients to determine objectives of photographic assignments
                  </li>
                  <li>
                    Studying requirements of an assignment and selecting the type of camera, film,
                    filter, and lighting
                  </li>
                  <li>
                    Working from a studio, and transporting and setting up equipment at assigned
                    locations
                  </li>
                  <li>Measuring light levels and determining exposure</li>
                  <li>
                    Adjusting camera angles, aperture settings, and subjects to achieve the desired
                    composition
                  </li>
                  <li>Adjusting lighting and filters to accentuate highlights and colours</li>
                  <li>Mixing photographic chemicals, processing film, and developing prints</li>
                  <li>
                    Manipulating images using digital imaging techniques and creative darkroom
                    techniques such as adjusting exposure and development times
                  </li>
                  <li>May make enlarged prints, and mount and frame prints</li>
                </ul>
                <p class="mb-5">
                  Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
                </p>
                <p class="mb-5">
                  <strong><u>Note:</u></strong> Now that digital photography has become standard,
                  the work that used to be done in the darkroom is typically accomplished on the
                  computer. You need to be familiar with Photo Editing Software. For example: Adobe
                  Photoshop
                </p>
              </div>
              <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
                <img
                  src="@/assets/images/experiences/creativeindustries/jo3.png"
                  alt="Photographers job outlook: weekly pay unavailable, strong future growth, 17,100 workers, high skill level, 48% full-time, 46 hours average full-time week, average age 38, 49% female."
                  class="mx-auto img-responsive"
                />
              </div>
            </div>
          </vx-card>
        </div>
      </vs-tab>
    </vs-tabs>
  </div>
</template>

<script>
export default {
  data() {
    return {
      activeTab: 0,
    };
  },
};
</script>
